import type { CSSProperties } from 'react';
import { useTranslation } from 'next-i18next';
import { ExclamationmarkSmallIcon, JellyIcon } from '@/icons';
import type { AvatarShapePart } from '@/shared/features/user-avatar';
import { cn } from '@/utils/tailwind.util';

/**
 * Figma 1506:37052 기준 카피.
 * defaultValue 는 S3 locale 캐시 갱신 전까지의 안전한 폴백이다.
 */
export const AVATAR_PURCHASE_COPY = {
  confirmTitle: {
    key: 'common:avatarDecorate.purchaseDialogTitle',
    defaultValue: '아이템을 구매할까요?'
  },
  insufficientTitle: {
    key: 'common:avatarDecorate.insufficientTitle',
    defaultValue: '젤리가 부족합니다'
  },
  purchaseAction: {
    key: 'common:avatarDecorate.purchaseAction',
    defaultValue: '구매'
  },
  myPoints: {
    key: 'common:avatarDecorate.myPointsLabel',
    defaultValue: '내 보유 젤리'
  },
  itemPrice: {
    key: 'common:avatarDecorate.itemPriceLabel',
    defaultValue: '아이템 가격'
  },
  /**
   * 시안 1811:87679.
   *
   * 옛 문구는 "부족한 젤리는 <내 클래스>에서 받을 수 있어요" 였는데, 링크를 눌러 클래스
   * 목록에 가도 학생이 할 수 있는 일이 없다 — 젤리는 **선생님이 주는 것**이고 학생은
   * 스스로 얻을 방법이 없다. 그래서 링크를 걷어내고 누구에게 말해야 하는지만 남긴다.
   *
   * ⚠️ 키를 새로 판 이유: 옛 키(`avatarDecorate.insufficientHelp`)는 로케일 시트에 이미
   * 옛 문구로 등록돼 있어서, defaultValue 만 바꾸면 시트 값이 이겨 화면은 그대로다.
   */
  insufficientHelp: {
    key: 'common:avatarDecorate.jellyFromTeacher',
    defaultValue: '젤리는 선생님께 받을 수 있어요'
  },
  /** 시안 1811:87679 첫 줄. 얼마나 모자란지를 **수치로** 말한다. */
  insufficientShortage: {
    key: 'common:avatarDecorate.insufficientShortage',
    defaultValue: '{{count}} 젤리가 부족합니다'
  },
  /** 시안 1811:87679 둘째 줄. 다음에 할 일을 알린다. */
  insufficientAction: {
    key: 'common:avatarDecorate.insufficientAction',
    defaultValue: '선생님께 젤리를 받아 아바타를 꾸며보세요'
  }
} as const;

// 정보 박스 한 줄(라벨 ↔ 값). 값 색만 상황에 따라 바뀐다.
const InfoRow = ({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) => <div className="flex h-[26px] items-center justify-between pl-4xs pr-3xs" data-sentry-component="InfoRow" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx">
    <span className="text-body-md-semibold text-neutral">{label}</span>
    <span className="flex shrink-0 items-center gap-2xs">{children}</span>
  </div>;
const COLOR_SWATCH_LIMIT = 5;
const EMPTY_COLOR_PARTS: AvatarShapePart[] = [];

/**
 * 아이템 구매 확인 / 포인트 부족 모달 본문 (Figma 1506:37052).
 *
 * 프리뷰 카드 → 정보 박스 순서. 두 상태가 같은 레이아웃을 쓰되:
 * - 구매 확인: 가격 → 내 보유 포인트. 보유 포인트 값은 strong.
 * - 포인트 부족: 가격 → 내 보유 포인트. 보유 포인트 값은 destructive.
 */
export const AvatarPurchaseConfirmBody = ({
  previewUrl,
  colorParts = EMPTY_COLOR_PARTS,
  price,
  balance,
  itemPriceLabel,
  isNew = false,
  isHot = false,
  isInsufficient = false
}: {
  previewUrl: string;
  colorParts?: AvatarShapePart[];
  price: number;
  balance: number;
  itemPriceLabel?: string;
  isNew?: boolean;
  isHot?: boolean;
  isInsufficient?: boolean;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const visibleColorParts = colorParts.slice(0, COLOR_SWATCH_LIMIT);
  const shouldShowColorParts = colorParts.length > 1;
  return <div className="flex flex-col gap-xs" data-sentry-component="AvatarPurchaseConfirmBody" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx">
      {isInsufficient &&
    // 시안 1811:87676 — 프리뷰 **위**다. 아래에 두면 아이템을 보고 가격을 읽은
    // 다음에야 못 산다는 걸 알게 된다. 결론을 먼저 말하고 근거를 보여준다.
    //
    // 링크가 아니라 안내다. 젤리를 얻는 유일한 경로가 선생님이라, 여기서 학생이
    // 누를 것은 없다 — 어디로 보내는 대신 누구에게 말해야 하는지만 알린다.
    <div className="flex w-full flex-col items-center justify-center gap-3xs rounded-sm bg-red-50 px-md pb-sm pt-xs text-center">
          <ExclamationmarkSmallIcon size="md" className="text-destructive" />
          <p className="text-body-md-semibold text-default">
            {t(AVATAR_PURCHASE_COPY.insufficientShortage.key, {
          defaultValue: AVATAR_PURCHASE_COPY.insufficientShortage.defaultValue,
          count: Math.max(price - balance, 0)
        })}
            <br />
            {t(AVATAR_PURCHASE_COPY.insufficientAction.key, {
          defaultValue: AVATAR_PURCHASE_COPY.insufficientAction.defaultValue
        })}
          </p>
        </div>}

      <div className="flex flex-col gap-[2px] overflow-hidden rounded-sm border border-opacity-black-2 bg-default">
        <div className={cn('relative flex w-full flex-col items-center justify-center overflow-hidden bg-neutral/50 pt-sm', shouldShowColorParts ? 'gap-xs pb-lg' : 'pb-xs')}>
          <img src={previewUrl} alt="" onError={event => {
          event.currentTarget.style.visibility = 'hidden';
        }} className="size-[80px] object-contain" />
          {(isNew || isHot) && <span className={cn('absolute right-[-1px] top-[-1px] z-[1] rounded-bl-xs px-3xs py-4xs text-caption-md-semibold text-inverse shadow-sm', isHot ? 'bg-gradient-to-r from-[#f268f5] to-[#ff737b]' : 'bg-gradient-to-r from-[#18d0b4] to-[#6ebfff]')}>
              {isHot ? 'HOT' : 'NEW'}
            </span>}
          {shouldShowColorParts && <div className="flex items-start gap-3xs">
              {visibleColorParts.map(part => <span key={part.partId} className="size-[10px] shrink-0 rounded-2xs border border-opacity" style={{
            '--avatar-purchase-color': part.hex ?? '#B8B9C8',
            backgroundColor: 'var(--avatar-purchase-color)'
          } as CSSProperties} />)}
            </div>}
        </div>

        <div className="flex w-full flex-col gap-xs bg-default p-md">
          <InfoRow label={itemPriceLabel ?? t(AVATAR_PURCHASE_COPY.itemPrice.key, {
          defaultValue: AVATAR_PURCHASE_COPY.itemPrice.defaultValue
        })} data-sentry-element="InfoRow" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx">
            <JellyIcon size="sm" data-sentry-element="JellyIcon" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx" />
            <span className="text-body-lg-bold text-neutral">{price}</span>
          </InfoRow>
          <InfoRow label={t(AVATAR_PURCHASE_COPY.myPoints.key, {
          defaultValue: AVATAR_PURCHASE_COPY.myPoints.defaultValue
        })} data-sentry-element="InfoRow" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx">
            <JellyIcon size="sm" data-sentry-element="JellyIcon" data-sentry-source-file="AvatarPurchaseConfirmBody.tsx" />
            <span className={cn('text-body-lg-bold',
          // Figma 1507-37821: 내 보유 젤리 값은 purple-600. 부족 시엔 경고로 destructive 유지.
          isInsufficient ? 'text-destructive' : 'text-purple-600')}>
              {balance}
            </span>
          </InfoRow>
        </div>
      </div>
    </div>;
};